import { BuilderElement, ElementType, GlobalConfig } from "@/types/builder";
import { Children } from "react";
import {
  createScientificReferencesSection,
  createScientificRefListingSection,
  createFooterMenuSection,
  createFooterEndSection,
} from "./template1";

const generateId = (): string => {
  return Math.random().toString(36).substr(2, 9);
};

export const template1UpsellGlobalConfig: GlobalConfig = {
  typography: [
    {
      name: "Default",
      fontFamily: "Inter",
      fontSize: "16px",
      fontWeight: "400",
      fontStyle: "normal",
    },
    {
      name: "Heading-1",
      fontFamily: "Inter",
      fontSize: "40px",
      fontWeight: "700",
      fontStyle: "normal",
    },
    {
      name: "Heading-2",
      fontFamily: "Inter",
      fontSize: "28px",
      fontWeight: "700",
      fontStyle: "normal",
    },
    {
      name: "Body",
      fontFamily: "Inter",
      fontSize: "18px",
      fontWeight: "400",
      fontStyle: "normal",
    },
    {
      name: "Button",
      fontFamily: "Inter",
      fontSize: "16px",
      fontWeight: "700",
      fontStyle: "normal",
    },
  ],
  colors: {
    background: "#F3F6FF",
    forground: "#FFFFFF",
    primary: "#A44CF5",
    secondary: "#317CF7",
    tertiary: "#333333",
    quaternary: "#1a1a1a",
    link: "#A44CF5",
    activeLink: "#1D4ED8",
  },
  breakpoints: {
    mobileMax: 640,
    tabletMin: 641,
    tabletMax: 1024,
    desktopMin: 1025,
  },
};

// Downsell Header Section
const createDownsellHeaderSectionContainer = (globalConfig) => ({
  id: generateId(),
  type: "container",
  styles: {
    padding: "135px 0",
    fontFamily: "SF Pro Display, sans-serif",
    backgroundImage:
      "linear-gradient(89deg, #01432A 10%, primary 30.18%, #869c2d 110.91%)",
    width: "100%",
  },
  responsiveStyles: {
    tablet: {
      padding: "80px 0",
    },
    mobile: {
      padding: "40px 0",
    },
  },
  children: [
    {
      id: generateId(),
      type: "container",
      styles: {
        maxWidth: "1084px",
        width: "100%",
        margin: "0 auto",
      },
      children: [
        {
          id: generateId(),
          type: "container",
          styles: {
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "flex-start",
            gap: "26px",
          },
          children: [
            {
              id: generateId(),
              type: "logo",
              styles: {
                maxWidth: "336px",
                width: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              },
              responsiveStyles: {
                tablet: {
                  maxWidth: "283px",
                },
              },
              children: [
                {
                  id: generateId(),
                  type: "image",
                  src: "/fortigold/logo-gold.webp",
                  alt: "Fortigold Logo",
                  styles: {
                    width: "100%",
                    height: "100%",
                    objectFit: "contain",
                  },
                },
              ],
            },
            {
              id: generateId(),
              type: "container",
              styles: {
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "center",
                gap: "8px",
              },
              children: [
                {
                  id: generateId(),
                  type: "text",
                  content: "WAIT!",
                  styles: {
                    color: "#FBB040",
                    fontSize: "62px",
                    fontWeight: 900,
                    lineHeight: "100%",
                    textAlign: "center",
                  },
                  responsiveStyles: {
                    tablet: {
                      fontSize: "40px",
                    },
                    mobile: {
                      fontSize: "32px",
                    },
                  },
                },
                {
                  id: generateId(),
                  type: "text",
                  content: "THIS IS YOUR FINAL CHANCE",
                  styles: {
                    color: "background",
                    fontSize: "62px",
                    fontWeight: 900,
                    lineHeight: "100%",
                    textAlign: "center",
                  },
                  responsiveStyles: {
                    tablet: {
                      fontSize: "40px",
                    },
                    mobile: {
                      fontSize: "32px",
                    },
                  },
                },
              ],
            },
          ],
        },
      ],
    },
  ],
});

// Black Add Section
const createDownsellBlackAddSectionContainer = (globalConfig) => ({
  id: generateId(),
  type: "container",
  styles: {
    backgroundColor: "#231F20",
    padding: "32px 0",
    fontFamily: "SF Pro Display, sans-serif",
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "1px",
  },
  responsiveStyles: {
    tablet: {
      padding: "20px 0",
    },
    mobile: {
      padding: "16px 0",
    },
  },
  children: [
    {
      id: generateId(),
      type: "container",
      styles: {
        maxWidth: "1084px",
        width: "100%",
        margin: "0 auto",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      },
      children: [
        {
          id: generateId(),
          type: "text",
          content: "3 Bottles Were Too Much? No Problem!",
          styles: {
            color: "background",
            fontSize: "38px",
            fontWeight: 700,
            lineHeight: "100%",
            textAlign: "center",
            width: "100%",
          },
          responsiveStyles: {
            tablet: {
              fontSize: "20px",
            },
            mobile: {
              fontSize: "18px",
            },
          },
        },
      ],
    },
  ],
});

// Features Section (Notes + Features Box in one container)
const createDownsellFeaturesSectionContainer = (globalConfig) => ({
  id: generateId(),
  type: "container",
  styles: {
    backgroundColor: "background",
    width: "100%",
    display: "flex",
    flexDirection: "column",
    gap: "60px",
    padding: "55px 0 0",
    fontFamily: "SF Pro Display, sans-serif",
  },
  responsiveStyles: {
    tablet: {
      padding: "35px 0 0",
      gap: "40px",
    },
    mobile: {
      padding: "25px 0 0",
      gap: "24px",
    },
  },
  children: [
    // Notes Section
    {
      id: generateId(),
      type: "container",
      styles: {
        width: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        gap: "55px",
        maxWidth: "1084px",
        margin: "0 auto",
      },
      responsiveStyles: {
        tablet: { gap: "35px" },
        mobile: { gap: "25px" },
      },
      children: [
        {
          id: generateId(),
          type: "heading",
          content: "It's Okay to Start Small...",
          styles: {
            color: "forground",
            fontSize: "58.3px",
            fontWeight: 700,
            lineHeight: "100%",
            margin: 0,
          },
          responsiveStyles: {
            mobile: { fontSize: "30px" },
          },
        },
        {
          id: generateId(),
          type: "paragraph",
          content:
            "We get it – 3 extra bottles might've felt like too much right now. But before we wrap up your order, here's a lighter option.",
          styles: {
            color: "#231F20",
            fontSize: "30px",
            fontWeight: 400,
            lineHeight: "100%",
            margin: 0,
          },
          responsiveStyles: {
            tablet: { fontSize: "24px" },
            mobile: { fontSize: "18px" },
          },
        },
        {
          id: generateId(),
          type: "paragraph",
          content:
            "Why not grab just 2 more bottles at the same VIP rate – no pressure, just one last little boost to keep your results going strong.",
          styles: {
            color: "#231F20",
            fontSize: "30px",
            fontWeight: 400,
            lineHeight: "100%",
            margin: 0,
          },
          responsiveStyles: {
            tablet: { fontSize: "24px" },
            mobile: { fontSize: "18px" },
          },
        },
      ],
    },
    // Features Box Section
    {
      id: generateId(),
      type: "container",
      styles: {
        width: "100%",
        margin: "100px 0 30px",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
      },
      responsiveStyles: {
        tablet: { margin: "60px 0 30px" },
        mobile: { margin: "30px 0" },
      },
      children: [
        {
          id: generateId(),
          type: "container",
          styles: {
            width: "100%",
            maxWidth: "1337.5px",

            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            background: "#fff",
          },
          children: [
            // Feature Header
            {
              id: generateId(),
              type: "container",
              styles: {
                backgroundImage:
                  "linear-gradient(90deg, rgb(134, 156, 45) 10.18%, rgb(0, 87, 63) 42.91%, #00442A 72%, #00442A 80%)",
                padding: "32px 0",
                textAlign: "center",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                width: "100%",
              },
              responsiveStyles: {
                mobile: { padding: "16px 0" },
              },
              children: [
                {
                  id: generateId(),
                  type: "container",
                  styles: {
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "8px",
                    width: "100%",
                  },
                  children: [
                    {
                      id: generateId(),
                      type: "heading",
                      content: "Final Chance: Add 2 More Bottles For Just",
                      dynamicContentId: "__dynamic__bottle_count__",
                      styles: {
                        color: "#FFF",
                        textAlign: "center",
                        fontSize: "40px",
                        fontWeight: 700,
                        lineHeight: "100%",
                        fontFamily: "SF Pro Display",
                        margin: 0,
                      },
                      responsiveStyles: {
                        mobile: { fontSize: "24px", lineHeight: "28px" },
                      },
                    },
                    {
                      id: generateId(),
                      type: "heading",
                      content: "$29 EACH",
                      dynamicContentId: "__dynamic__offer__",
                      styles: {
                        color: "#FFDE17",
                        textAlign: "center",
                        fontSize: "40px",
                        fontWeight: 700,
                        lineHeight: "100%",
                        fontFamily: "SF Pro Display",
                        margin: 0,
                      },
                      responsiveStyles: {
                        mobile: { fontSize: "24px", lineHeight: "28px" },
                      },
                    },
                  ],
                },
              ],
            },
            // Feature Body
            {
              id: generateId(),
              type: "container",
              styles: {
                padding: "40px",
                backgroundColor: "#DBDBDB",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "100%",
                gap: "32px",
              },
              responsiveStyles: {
                tablet: {
                  flexDirection: "column",
                  padding: "20px",
                  gap: "16px",
                },
                mobile: {
                  padding: "10px",
                  gap: "8px",
                  flexDirection: "column",
                },
              },
              children: [
                // Image
                {
                  id: generateId(),
                  type: "container",
                  styles: {
                    flex: "1 1 50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  },
                  children: [
                    {
                      id: generateId(),
                      type: "image",
                      src: "/fortigold/bottle2.webp",
                      alt: "2 Bottles",
                      dynamicContentId: "__dynamic__bottle_image__",
                      styles: {
                        maxWidth: "90%",
                        width: "100%",
                        margin: "0 auto",
                        objectFit: "contain",
                      },
                      responsiveStyles: {
                        mobile: { maxWidth: "100%" },
                      },
                    },
                  ],
                },
                // Content Body
                {
                  id: generateId(),
                  type: "container",
                  styles: {
                    flex: "1 1 50%",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-start",
                    gap: "30px",
                  },
                  responsiveStyles: {
                    mobile: { gap: "16px" },
                  },
                  children: [
                    // Feature List
                    {
                      id: generateId(),
                      type: "container",
                      styles: {
                        display: "flex",
                        flexDirection: "column",
                        gap: "20px",
                      },
                      responsiveStyles: {
                        mobile: { gap: "16px" },
                      },
                      children: [
                        {
                          id: generateId(),
                          type: "text",
                          content:
                            "This is your LAST chance to get our best pricing before your order ships.",
                          styles: {
                            color: "#231F20",
                            fontSize: "24px",
                            fontWeight: 600,
                            lineHeight: "26px",
                          },
                          responsiveStyles: {
                            tablet: { fontSize: "22px", lineHeight: "30px" },
                            mobile: { fontSize: "18px", lineHeight: "24px" },
                          },
                        },
                        // List items
                        {
                          id: generateId(),
                          type: "container",
                          styles: {
                            display: "flex",
                            alignItems: "flex-start",
                            gap: "10px",
                          },
                          children: [
                            {
                              id: generateId(),
                              type: "text",
                              content: "✅",
                              styles: { fontSize: "24px" },
                            },
                            {
                              id: generateId(),
                              type: "text",
                              content:
                                "Extend your progress just a little further",
                              styles: {
                                color: "#231F20",
                                fontSize: "24px",
                                fontWeight: 600,
                                lineHeight: "26px",
                              },
                              responsiveStyles: {
                                tablet: {
                                  fontSize: "22px",
                                  lineHeight: "30px",
                                },
                                mobile: {
                                  fontSize: "18px",
                                  lineHeight: "24px",
                                },
                              },
                            },
                          ],
                        },
                        {
                          id: generateId(),
                          type: "container",
                          styles: {
                            display: "flex",
                            alignItems: "flex-start",
                            gap: "10px",
                          },
                          children: [
                            {
                              id: generateId(),
                              type: "text",
                              content: "✅",
                              styles: { fontSize: "24px" },
                            },
                            {
                              id: generateId(),
                              type: "text",
                              content:
                                "Stay consistent during your first 60 days\nCovered by our 100% money-back guarantee",
                              styles: {
                                color: "#231F20",
                                fontSize: "24px",
                                fontWeight: 600,
                                lineHeight: "26px",
                              },
                              responsiveStyles: {
                                tablet: {
                                  fontSize: "22px",
                                  lineHeight: "30px",
                                },
                                mobile: {
                                  fontSize: "18px",
                                  lineHeight: "24px",
                                },
                              },
                            },
                          ],
                        },
                      ],
                    },
                    // CTA Button
                    {
                      id: generateId(),
                      type: "button",
                      content: "YES Add 2 More Bottles for $58",
                      dynamicContentId: "__dynamic__button_offer__",
                      styles: {
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        padding: "0 35px",
                        height: "74px",
                        borderRadius: "14.568px",
                        border: "3.642px solid secondary",
                        backgroundImage:
                          "conic-gradient(from 72deg at 33.42% 100%, #F49D00 5.4deg, #FAC600 196.2deg, #FCC709 360deg)",
                        color: "#231F20",
                        textAlign: "center",
                        fontSize: "24px",
                        fontWeight: 700,
                        lineHeight: "100%",
                        fontFamily: "SF Pro Display",

                        margin: "16px 0 0 0",
                        cursor: "pointer",
                      },
                      responsiveStyles: {
                        mobile: { fontSize: "16px", height: "50px" },
                      },
                    },
                    // Guarantee
                    {
                      id: generateId(),
                      type: "text",
                      content:
                        "100% MONEY BACK GUARANTEE - NO QUESTIONS ASKED!",
                      styles: {
                        color: "#231F20",
                        fontSize: "18px",
                        fontWeight: 400,
                        textAlign: "center",
                        width: "100%",
                        margin: "8px 0 0 0",
                      },
                    },
                    // Payment Methods Image
                    {
                      id: generateId(),
                      type: "image",
                      src: "/public/images/movyo/pay-option1.webp",
                      alt: "Payment Methods",
                      styles: {
                        maxWidth: "324px",
                        width: "100%",
                        objectFit: "contain",
                        margin: "8px 0 0 0",
                      },
                    },
                  ],
                },
              ],
            },
            // Feature Box Text (No thanks link)
            {
              id: generateId(),
              type: "link",
              content: "No thanks, I'll pass and risk losing my results later.",
              href: "#",
              styles: {
                color: "#231F20",
                textAlign: "center",
                fontSize: "22px",
                fontStyle: "italic",
                fontWeight: 400,
                lineHeight: "100%",
                fontFamily: "SF Pro Display",
                width: "100%",
                margin: "24px 0 0 0",
                textDecoration: "underline",
                cursor: "pointer",
              },
              responsiveStyles: {
                mobile: { fontSize: "16px" },
              },
            },
          ],
        },
      ],
    },
  ],
});

const createDownsellMessageBoxSectionContainer = (globalConfig) => ({
  id: generateId(),
  type: "container",
  styles: {
    backgroundColor: "background",
    width: "100%",
    margin: "90px 0 100px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    fontFamily: "SF Pro Display, sans-serif",
  },
  responsiveStyles: {
    tablet: { margin: "60px 0" },
    mobile: { margin: "45px 0" },
  },
  children: [
    {
      id: generateId(),
      type: "container",
      styles: {
        width: "100%",
        maxWidth: "1337.5px",
        margin: "0 auto",
        display: "flex",
        flexDirection: "row",
        alignItems: "stretch",
        justifyContent: "center",
        gap: "0 63px",
      },
      responsiveStyles: {
        tablet: { flexDirection: "column", gap: "30px 0" },
        mobile: { flexDirection: "column", gap: "16px 0" },
      },
      children: [
        // Doctor Image
        {
          id: generateId(),
          type: "image",
          src: "/fortigold/doctor.webp",
          alt: "Dr. James Mitchell",
          styles: {
            maxWidth: "400px",
            width: "100%",
            objectFit: "cover",
            objectPosition: "top",
            margin: "auto",
            borderRadius: "0",
          },
          responsiveStyles: {
            tablet: { maxWidth: "100%", height: "300px" },
            mobile: { maxWidth: "100%", height: "auto" },
          },
        },
        // Message Content
        {
          id: generateId(),
          type: "container",
          styles: {
            flex: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            gap: "24px",
            width: "100%",
          },
          responsiveStyles: {
            tablet: { gap: "16px" },
            mobile: { gap: "12px" },
          },
          children: [
            // Title
            {
              id: generateId(),
              type: "heading",
              content: "A Final Thought From Dr. James Mitchell",
              styles: {
                color: "forground",
                fontSize: "32px",
                fontWeight: 700,
                lineHeight: "120%",
                margin: 0,
              },
              responsiveStyles: {
                tablet: { fontSize: "28px" },
                mobile: { fontSize: "22px" },
              },
            },
            // Paragraphs
            {
              id: generateId(),
              type: "paragraph",
              content:
                "If you've made it this far, it means you're ready for change – and truly respect that. Starting with one bottle of FortiGold is a smart first step. But as a doctor who's worked with thousands of patients, I can tell you: one bottle is rarely enough.",
              styles: {
                color: "forground",
                fontSize: "18px",
                fontWeight: 400,
                lineHeight: "150%",
                margin: 0,
              },
              responsiveStyles: {
                tablet: { fontSize: "16px" },
                mobile: { fontSize: "14px" },
              },
            },
            {
              id: generateId(),
              type: "paragraph",
              content:
                "Your body needs time. Time to reset, rebuild, and truly respond. That's why I created this small follow-up option - not to pressure you, but to give you a real shot at seeing deeper results.",
              styles: {
                color: "#000",
                fontSize: "18px",
                fontWeight: 400,
                lineHeight: "150%",
                margin: 0,
              },
              responsiveStyles: {
                tablet: { fontSize: "16px" },
                mobile: { fontSize: "14px" },
              },
            },
            {
              id: generateId(),
              type: "paragraph",
              content:
                "Just one more bottle can make the difference between 'starting' and truly transforming.",
              styles: {
                color: "#000",
                fontSize: "18px",
                fontWeight: 400,
                lineHeight: "150%",
                margin: 0,
              },
              responsiveStyles: {
                tablet: { fontSize: "16px" },
                mobile: { fontSize: "14px" },
              },
            },
            {
              id: generateId(),
              type: "paragraph",
              content:
                '"If you can, take this last chance. Your future self might be grateful you did."',
              styles: {
                color: "#000",
                fontSize: "18px",
                fontWeight: 400,
                fontStyle: "italic",
                lineHeight: "150%",
                margin: 0,
              },
              responsiveStyles: {
                tablet: { fontSize: "16px" },
                mobile: { fontSize: "14px" },
              },
            },
            // Signature
            {
              id: generateId(),
              type: "container",
              styles: {
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                gap: "0",
                marginTop: "16px",
              },
              children: [
                {
                  id: generateId(),
                  type: "text",
                  content: "Dr. James Mitchell",
                  styles: {
                    fontFamily: "Allura, cursive",
                    fontSize: "42px",
                    color: "#231F20",
                    fontWeight: 400,
                    margin: 0,
                  },
                  responsiveStyles: {
                    tablet: { fontSize: "30px", lineHeight: "42px" },
                    mobile: { fontSize: "24px" },
                  },
                },
                {
                  id: generateId(),
                  type: "text",
                  content: "Formulator of FortiGold",
                  styles: {
                    color: "#231F20",
                    fontFamily: "SF Pro Display",
                    fontSize: "28px",
                    fontWeight: 400,
                    lineHeight: "100%",
                    margin: 0,
                  },
                  responsiveStyles: {
                    tablet: { fontSize: "24px" },
                    mobile: { fontSize: "18px" },
                  },
                },
              ],
            },
          ],
        },
      ],
    },
  ],
});

// Features2 Section (reuse the same card as in the first features section, no notes)
const createDownsellFeatures2SectionContainer = (globalConfig) => ({
  id: generateId(),
  type: "container",
  styles: {
    width: "100%",
    margin: "100px 0 30px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
  },
  responsiveStyles: {
    tablet: { margin: "60px 0 30px" },
    mobile: { margin: "30px 0" },
  },
  children: [
    {
      id: generateId(),
      type: "container",
      styles: {
        width: "100%",
        maxWidth: "1337.5px",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        background: "#fff",
      },
      children: [
        // Feature Header
        {
          id: generateId(),
          type: "container",
          styles: {
            backgroundImage:
              "linear-gradient(90deg, rgb(134, 156, 45) 10.18%, rgb(0, 87, 63) 42.91%, #00442A 72%, #00442A 80%)",
            padding: "32px 0",
            textAlign: "center",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            width: "100%",
          },
          responsiveStyles: {
            mobile: { padding: "16px 0" },
          },
          children: [
            {
              id: generateId(),
              type: "container",
              styles: {
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "center",
                gap: "8px",
                width: "100%",
              },
              children: [
                {
                  id: generateId(),
                  type: "heading",
                  content: "Final Chance: Add 2 More Bottles For Just",
                  dynamicContentId: "__dynamic__bottle_count__",
                  styles: {
                    color: "background",
                    textAlign: "center",
                    fontSize: "40px",
                    fontWeight: 700,
                    lineHeight: "100%",
                    fontFamily: "SF Pro Display",
                    margin: 0,
                  },
                  responsiveStyles: {
                    mobile: { fontSize: "24px", lineHeight: "28px" },
                  },
                },
                {
                  id: generateId(),
                  type: "heading",
                  content: "$29 EACH",
                  styles: {
                    color: "#FFDE17",
                    textAlign: "center",
                    fontSize: "40px",
                    fontWeight: 700,
                    lineHeight: "100%",
                    fontFamily: "SF Pro Display",
                    margin: 0,
                  },
                  responsiveStyles: {
                    mobile: { fontSize: "24px", lineHeight: "28px" },
                  },
                },
              ],
            },
          ],
        },
        // Feature Body
        {
          id: generateId(),
          type: "container",
          styles: {
            padding: "40px",
            backgroundColor: "#DBDBDB",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: "100%",
            gap: "32px",
          },
          responsiveStyles: {
            tablet: { flexDirection: "column", padding: "20px", gap: "16px" },
            mobile: { padding: "10px", gap: "8px", flexDirection: "column" },
          },
          children: [
            // Image
            {
              id: generateId(),
              type: "container",
              styles: {
                flex: "1 1 50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              },
              children: [
                {
                  id: generateId(),
                  dynamicContentId: "__dynamic__bottle_image__",
                  type: "image",
                  src: "/fortigold/bottle2.webp",
                  alt: "2 Bottles",
                  styles: {
                    maxWidth: "90%",
                    width: "100%",
                    margin: "0 auto",
                    objectFit: "contain",
                  },
                  responsiveStyles: {
                    mobile: { maxWidth: "100%" },
                  },
                },
              ],
            },
            // Content Body
            {
              id: generateId(),
              type: "container",
              styles: {
                flex: "1 1 50%",
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                gap: "30px",
              },
              responsiveStyles: {
                mobile: { gap: "16px" },
              },
              children: [
                // Feature List
                {
                  id: generateId(),
                  type: "container",
                  styles: {
                    display: "flex",
                    flexDirection: "column",
                    gap: "20px",
                  },
                  responsiveStyles: {
                    mobile: { gap: "16px" },
                  },
                  children: [
                    {
                      id: generateId(),
                      type: "text",
                      content:
                        "This is your LAST chance to get our best pricing before your order ships.",
                      styles: {
                        color: "#231F20",
                        fontSize: "24px",
                        fontWeight: 600,
                        lineHeight: "26px",
                      },
                      responsiveStyles: {
                        tablet: { fontSize: "22px", lineHeight: "30px" },
                        mobile: { fontSize: "18px", lineHeight: "24px" },
                      },
                    },
                    // List items
                    {
                      id: generateId(),
                      type: "container",
                      styles: {
                        display: "flex",
                        alignItems: "flex-start",
                        gap: "10px",
                      },
                      children: [
                        {
                          id: generateId(),
                          type: "text",
                          content: "✅",
                          styles: { fontSize: "24px" },
                        },
                        {
                          id: generateId(),
                          type: "text",
                          content: "Extend your progress just a little further",
                          styles: {
                            color: "#231F20",
                            fontSize: "24px",
                            fontWeight: 600,
                            lineHeight: "26px",
                          },
                          responsiveStyles: {
                            tablet: { fontSize: "22px", lineHeight: "30px" },
                            mobile: { fontSize: "18px", lineHeight: "24px" },
                          },
                        },
                      ],
                    },
                    {
                      id: generateId(),
                      type: "container",
                      styles: {
                        display: "flex",
                        alignItems: "flex-start",
                        gap: "10px",
                      },
                      children: [
                        {
                          id: generateId(),
                          type: "text",
                          content: "✅",
                          styles: { fontSize: "24px" },
                        },
                        {
                          id: generateId(),
                          type: "text",
                          content:
                            "Stay consistent during your first 60 days\nCovered by our 100% money-back guarantee",
                          styles: {
                            color: "#231F20",
                            fontSize: "24px",
                            fontWeight: 600,
                            lineHeight: "26px",
                          },
                          responsiveStyles: {
                            tablet: { fontSize: "22px", lineHeight: "30px" },
                            mobile: { fontSize: "18px", lineHeight: "24px" },
                          },
                        },
                      ],
                    },
                  ],
                },
                // CTA Button
                {
                  id: generateId(),
                  type: "button",
                  content: "YES Add 2 More Bottles for $58",
                  dynamicContentId: "__dynamic__button_offer__",
                  styles: {
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: "0 35px",
                    height: "74px",
                    borderRadius: "14.568px",
                    border: "3.642px solid secondary",
                    backgroundImage:
                      "conic-gradient(from 72deg at 33.42% 100%, #F49D00 5.4deg, #FAC600 196.2deg, #FCC709 360deg)",
                    color: "#231F20",
                    textAlign: "center",
                    fontSize: "24px",
                    fontWeight: 700,
                    lineHeight: "100%",
                    fontFamily: "SF Pro Display",
                    margin: "16px 0 0 0",
                    cursor: "pointer",
                  },
                  responsiveStyles: {
                    mobile: { fontSize: "16px", height: "50px" },
                  },
                },
                // Guarantee
                {
                  id: generateId(),
                  type: "text",
                  content: "100% MONEY BACK GUARANTEE - NO QUESTIONS ASKED!",
                  styles: {
                    color: "#231F20",
                    fontSize: "18px",
                    fontWeight: 400,
                    textAlign: "center",
                    width: "100%",
                    margin: "8px 0 0 0",
                  },
                },
                // Payment Methods Image
                {
                  id: generateId(),
                  type: "image",
                  src: "/public/images/movyo/pay-option1.webp",
                  alt: "Payment Methods",
                  styles: {
                    maxWidth: "324px",
                    width: "100%",
                    objectFit: "contain",
                    margin: "8px 0 0 0",
                  },
                },
              ],
            },
          ],
        },
        // Feature Box Text (No thanks link)
        {
          id: generateId(),
          type: "link",
          content: "No thanks, I'll pass and risk losing my results later.",
          href: "#",
          styles: {
            color: "#231F20",
            textAlign: "center",
            fontSize: "22px",
            fontStyle: "italic",
            fontWeight: 400,
            lineHeight: "100%",
            fontFamily: "SF Pro Display",
            width: "100%",
            margin: "24px 0 0 0",
            textDecoration: "underline",
            cursor: "pointer",
          },
          responsiveStyles: {
            mobile: { fontSize: "16px" },
          },
        },
      ],
    },
  ],
});

export const template1Downsell = (globalConfig: GlobalConfig) => {
  return {
    headerAlertMsg: createDownsellHeaderSectionContainer(globalConfig),
    blackAdd: createDownsellBlackAddSectionContainer(globalConfig),
    features: createDownsellFeaturesSectionContainer(globalConfig),
    messageBox: createDownsellMessageBoxSectionContainer(globalConfig),
    features2: createDownsellFeatures2SectionContainer(globalConfig),
    scientificReferencesSection:
      createScientificReferencesSection(globalConfig),
    scientificRefListingSection:
      createScientificRefListingSection(globalConfig),
    footerMenuSection: createFooterMenuSection(globalConfig),
    footerEndSection: createFooterEndSection(globalConfig),
  };
};
